import { supabase } from '../lib/supabase';

export const routeService = {
  async getAll() {
    const { data, error } = await supabase?.from('routes')?.select('*')?.order('route_name', { ascending: true });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      routeName: row?.route_name,
      startPoint: row?.start_point,
      endPoint: row?.end_point,
      isActive: row?.is_active,
      geometry: row?.geometry,
      distanceKm: row?.distance_km,
      durationMinutes: row?.duration_minutes,
      departureTimes: row?.departure_times || [],
      lastModified: row?.last_modified,
      createdAt: row?.created_at
    })) || [];
  },

  async getById(id) {
    const { data, error } = await supabase?.from('routes')?.select('*')?.eq('id', id)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      routeName: data?.route_name,
      startPoint: data?.start_point,
      endPoint: data?.end_point,
      isActive: data?.is_active,
      geometry: data?.geometry,
      distanceKm: data?.distance_km,
      durationMinutes: data?.duration_minutes,
      departureTimes: data?.departure_times || [],
      lastModified: data?.last_modified,
      createdAt: data?.created_at
    };
  },

  async getActiveRoutes() {
    const { data, error } = await supabase?.from('routes')?.select('*')?.eq('is_active', true)?.order('route_name', { ascending: true });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      routeName: row?.route_name,
      startPoint: row?.start_point,
      endPoint: row?.end_point,
      isActive: row?.is_active,
      geometry: row?.geometry,
      distanceKm: row?.distance_km,
      durationMinutes: row?.duration_minutes,
      departureTimes: row?.departure_times || [],
      lastModified: row?.last_modified,
      createdAt: row?.created_at
    })) || [];
  },

  async create(route) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('routes')?.insert({
        route_name: route?.routeName,
        start_point: route?.startPoint,
        end_point: route?.endPoint,
        is_active: route?.isActive ?? true,
        geometry: route?.geometry,
        distance_km: route?.distanceKm,
        duration_minutes: route?.durationMinutes,
        departure_times: route?.departureTimes || [],
        created_by: user?.id
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      routeName: data?.route_name,
      startPoint: data?.start_point,
      endPoint: data?.end_point,
      isActive: data?.is_active,
      geometry: data?.geometry,
      distanceKm: data?.distance_km,
      durationMinutes: data?.duration_minutes,
      departureTimes: data?.departure_times || [],
      lastModified: data?.last_modified,
      createdAt: data?.created_at
    };
  },

  async update(id, route) {
    const { data, error } = await supabase?.from('routes')?.update({
        route_name: route?.routeName,
        start_point: route?.startPoint,
        end_point: route?.endPoint,
        is_active: route?.isActive,
        geometry: route?.geometry,
        distance_km: route?.distanceKm,
        duration_minutes: route?.durationMinutes,
        departure_times: route?.departureTimes || [],
        last_modified: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      routeName: data?.route_name,
      startPoint: data?.start_point,
      endPoint: data?.end_point,
      isActive: data?.is_active,
      geometry: data?.geometry,
      distanceKm: data?.distance_km,
      durationMinutes: data?.duration_minutes,
      departureTimes: data?.departure_times || [],
      lastModified: data?.last_modified,
      createdAt: data?.created_at
    };
  },

  async delete(id) {
    const { error } = await supabase?.from('routes')?.delete()?.eq('id', id);

    if (error) throw error;
  },

  async bulkDelete(ids) {
    const { error } = await supabase?.from('routes')?.delete()?.in('id', ids);

    if (error) throw error;
  },

  async bulkUpdateStatus(ids, isActive) {
    const { error } = await supabase?.from('routes')?.update({ is_active: isActive })?.in('id', ids);

    if (error) throw error;
  },

  // Calculate ETA based on user location and route geometry
  calculateETA(userLocation, routeGeometry, departureTime) {
    if (!routeGeometry?.coordinates || routeGeometry?.coordinates?.length < 2) {
      return null;
    }

    // Find closest point on route to user location
    const closestPoint = this.findClosestPointOnRoute(userLocation, routeGeometry?.coordinates);
    if (!closestPoint) return null;

    // Calculate distance from closest point to end of route
    const distanceToEnd = this.calculateDistanceAlongRoute(
      closestPoint?.index,
      routeGeometry?.coordinates
    );

    // Average minibus speed (km/h) - adjust based on traffic conditions
    const averageSpeed = 30; // 30 km/h in city traffic
    
    // Calculate travel time in minutes
    const travelTimeMinutes = (distanceToEnd / 1000) / averageSpeed * 60;

    // Parse departure time
    const now = new Date();
    const [hours, minutes] = departureTime?.split(':')?.map(Number);
    const departure = new Date(now);
    departure?.setHours(hours, minutes, 0, 0);

    // If departure time has passed, assume next day
    if (departure < now) {
      departure?.setDate(departure?.getDate() + 1);
    }

    // Calculate arrival time
    const arrivalTime = new Date(departure.getTime() + travelTimeMinutes * 60000);
    const minutesUntilArrival = Math.round((arrivalTime - now) / 60000);

    return {
      distanceMeters: Math.round(distanceToEnd),
      travelTimeMinutes: Math.round(travelTimeMinutes),
      departureTime: departure,
      arrivalTime: arrivalTime,
      minutesUntilArrival: minutesUntilArrival > 0 ? minutesUntilArrival : 0,
      closestPointIndex: closestPoint?.index
    };
  },

  // Find closest point on route to user location
  findClosestPointOnRoute(userLocation, routeCoordinates) {
    let minDistance = Infinity;
    let closestIndex = 0;

    routeCoordinates?.forEach((coord, index) => {
      const [lng, lat] = coord;
      const distance = this.getDistanceBetweenPoints(
        userLocation?.lat,
        userLocation?.lng,
        lat,
        lng
      );

      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    return {
      index: closestIndex,
      distance: minDistance,
      coordinates: routeCoordinates?.[closestIndex]
    };
  },

  // Calculate distance along route from a specific point to the end
  calculateDistanceAlongRoute(startIndex, routeCoordinates) {
    let totalDistance = 0;

    for (let i = startIndex; i < routeCoordinates?.length - 1; i++) {
      const [lng1, lat1] = routeCoordinates?.[i];
      const [lng2, lat2] = routeCoordinates?.[i + 1];
      totalDistance += this.getDistanceBetweenPoints(lat1, lng1, lat2, lng2);
    }

    return totalDistance;
  },

  // Haversine formula for distance calculation
  getDistanceBetweenPoints(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // Earth radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }
};